import { BaseColorPicker } from "../../../ColorPicker/BaseColorPicker";
import { ColorPickerProps } from "../../../ColorPicker/ColorPicker";
import { DropOverInput } from "../../../DropOverInput/DropOverInput";
import {
	ReactElement,
	ReadonlyRefObject,
	RefObject,
	useCallback,
	useRef,
	useState
} from "react";
import styles from "./ApplyColorControl.module.less";
import classNames from "classnames";
import { useSynchronizedRef } from "client/Hooks";
import { Guid, newGuid } from "client_legacy/ndaw/guid";

export type ApplyColorControlProps = Omit<ColorPickerProps, "onChange"> & {
	onChange(value: string | undefined, isSignificant: boolean): void;
	isDisabled?: boolean;
};

export const ApplyColorControl = (props: ApplyColorControlProps): ReactElement => {
	const {
		value,
		onChange,
		isDisabled = false,
		tooltip,
		...restOfTheProps
	} = props;

	const [dropOverId] = useState<Guid>(() => newGuid());
	const [lastPickedColor, setLastPickedColor] = useState<string | undefined>(value ?? undefined);
	const lastPickedColorRef: ReadonlyRefObject<string | undefined> = useSynchronizedRef(lastPickedColor);
	const buttonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const changeHandler = useCallback(
		(newValue: string | undefined) => {
			onChange(newValue, false);
			setLastPickedColor(newValue);
		},
		[onChange]
	);

	const significantChangeHandler = useCallback(
		(): void => {
			onChange(lastPickedColorRef.current, true);
		},
		[
			lastPickedColorRef,
			onChange
		]
	);

	const focusPreviewHandler = useCallback(
		(): void => {
			buttonRef.current
				?.focus();
		},
		[]
	);

	const renderPreview = (
		_color: string | undefined,
		isOpen: boolean,
		onClick: () => void
	): ReactElement => {
		return (
			<DropOverInput
				isActive={isOpen}
				actions={[
					{
						icon: "chevron-down",
						onClick
					}
				]}
				isDisabled={isDisabled}
			>
				<button
					style={!isDisabled
						? {
							textDecorationColor: lastPickedColor
						}
						: undefined
					}
					className={classNames(
						styles.applyColorButton,
						{
							[styles.applyColorButton_disabled]: isDisabled
						}
					)}
					type="button"
					aria-haspopup
					aria-expanded={isOpen}
					aria-controls={dropOverId}
					aria-label={tooltip}
					onClick={significantChangeHandler}
				>
					A
				</button>
			</DropOverInput>
		);
	};

	return (
		<BaseColorPicker
			{...restOfTheProps}
			tooltip={tooltip}
			onChange={changeHandler}
			value={value}
			renderPreview={renderPreview}
			onClosed={significantChangeHandler}
			onFocusPreview={focusPreviewHandler}
			dropOverId={dropOverId}
		/>
	);
};
ApplyColorControl.displayName="ApplyColorControl";